import { type Partner } from '@mzc-watchover/adminapp-appsync'
import { type ConfigProps } from 'components/Dashboard'
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputAdornment,
  OutlinedInput,
  Typography
} from '@mui/material'
import { CheckboxGroupInput, useTranslate } from 'react-admin'
import { filter, isEmpty, isNil, keys, map, toNumber } from 'lodash/fp'
import { useFormContext } from 'react-hook-form'
import log from 'loglevel'
import React from 'react'
import { postDemandAlertSettings } from 'components/DataProvider/functions'
import { useGetDemandAlartSettings } from 'components/DataProvider'
import { useNotify } from 'components/Util'

export const DemandAlert: React.FC<
  ConfigProps & { partner: Partial<Partner> }
> = ({ config, partner }) => {
  const { group } = config.Accounts
  const targetKeys = filter(
    (key: string) => key !== 'admin' && key !== 'partner'
  )(keys(group))
  const translate = useTranslate()
  const [threshold, setThreshold] = React.useState(90)
  const { setValue, getValues } = useFormContext()
  const notify = useNotify()
  const { data, isLoading } = useGetDemandAlartSettings(partner.id as string)
  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (isLoading || isNil(data)) return
    const { threshold, targets } = data
    log.info({ data })
    setThreshold(threshold)
    setValue('targets', targets, { shouldDirty: true, shouldValidate: true })
  }, [data, isLoading])
  const handleRegister = (): void => {
    const { targets } = getValues()
    postDemandAlertSettings(partner.id as string, threshold, targets)
      .then(() => {
        notify('resources.demandAlert.notification.done')
      })
      .catch(() => {
        notify('resources.demandAlert.notification.error')
      })
  }
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (isLoading) return null
  const { targets: targetsValue } = getValues()
  return (
    <Grid container>
      <Grid size={12}>
        <Typography variant="h6">
          {translate('resources.demandAlert.labels.threshold')}
        </Typography>
      </Grid>
      <Grid size={12}>
        <FormControl variant="outlined">
          <OutlinedInput
            type="number"
            value={threshold}
            endAdornment={<InputAdornment position="end">%</InputAdornment>}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = event.target
              setThreshold(toNumber(value))
            }}
          />
        </FormControl>
      </Grid>
      <Grid size={12} sx={{ mt: '1em' }}>
        <Typography variant="h6">
          {translate('resources.demandAlert.labels.targets')}
        </Typography>
      </Grid>
      <Grid size={12}>
        <CheckboxGroupInput
          source="targets"
          label={false}
          row={false}
          choices={map((key: string) => {
            return {
              id: key,
              name: group[key]
            }
          })(targetKeys)}
        />
      </Grid>
      <Grid size={12}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1em'
          }}>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={isNil(targetsValue) || isEmpty(targetsValue)}>
            {translate('resources.demandAlert.buttons.register')}
          </Button>
        </Box>
      </Grid>
    </Grid>
  )
}
